import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import {
  DeferredPost,
  DeferredPostSource,
  NetworkType,
  PostTemplate,
  PostTemplatesSearchResult,
  TopicsWithMetadataResponse,
  TopicWithMetadata,
} from '@3m5/sm_publish-frontend-api'
import { DeferredPostStatus } from '@3m5/sm_publish-frontend-api'
import { PublishedPost } from '@3m5/sm_publish-frontend-api'
import { Box, Divider, Grid, Paper, Stack, Typography } from '@mui/material'
import Chip from '@mui/material/Chip'
import TemplatePost from './AddOns/TemplatePost'
import formatDate from '@professocial/professocialFormComponents/FormComponents/util/formatDate'
import getDateFormat from '../../../defaults/getDateFormat'
import ErrorIcon from '@mui/icons-material/Error'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import VerticalSplitOutlined from '@mui/icons-material/VerticalSplitOutlined'
import { FilterType, PostingScreen } from '../../types/types'
import EditIcon from '@mui/icons-material/Edit'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { deletePostTemplateInitiated, setUpdateTemplateId } from '../../../store/posting/posting.actions'
import { getAboTopics, getUpdateTemplateId } from '../../../store/posting/posting.selector'
import Statistic from './Post/modules/Statistic'
import Trash from '../../icons/Trash'
import Preloader from '@professocial/professocialFormComponents/FormComponents/Preloader'
import { ActionCall, ViewEnum } from '@professocial/professocialTypes/types/commonTypes'
import { PostingRootState } from '../../../store/postingRoot.reducer'
import { namedRequestData } from '../../../store/common/common.selectors'

type Props = {
  user: any
  posting?: PostTemplate
  filterType?: FilterType
  deferredPosting?: DeferredPost
  templateData?: PostTemplatesSearchResult
  setUpdateTemplate?: (id: string) => void
  changePosting: (post: PostTemplate | undefined, deferredPost: DeferredPost | undefined) => void
  changePostingScreen?: (value: PostingScreen) => void
  deletePosting?: (templateID: string) => void
}

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    width: '100%',
    padding: theme.spacing(4),
    marginBottom: '10px',
    scrollMarginTop: '132px', // header height
  },
  button: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.body1.fontSize,
    textTransform: 'initial',
    marginLeft: '20px',
  },
  icon: {
    verticalAlign: 'top',
    fontSize: '26px'
  },
  noComments: {
    flex: '0 0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  statusColorError: {
    color: theme.palette.error.main,
    marginRight: theme.spacing(),
  },
  statusColorInfo: {
    color: theme.palette.warning.main,
    marginRight: theme.spacing(),
  },
  flex: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  itemContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
    paddingTop: '9px',
    gap: '5px',
    marginRight: theme.spacing(),
  },
  item: {
    verticalAlign: 'top',
    fill: theme.palette.info.main,
  },
  container: {
    position: 'relative',
  },
  backgroundLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgb(255,255,255,0.6)',
  },
  preloader: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%,-50%)',
  },
}))

const ShowPostTemplate: FC<Props> = (props) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const { deferredPosting, posting, templateData } = props
  const ref = useRef<HTMLDivElement>(null)
  const dispatch = useDispatch()

  const dispatchSetTemplateID = useCallback((props: string) => dispatch(setUpdateTemplateId(props)), [dispatch])

  const templateID: string | undefined = useSelector(getUpdateTemplateId)
  const storedCategories: TopicsWithMetadataResponse | undefined = useSelector(getAboTopics)
  const actionOnTemplateDelete: ActionCall | undefined = useSelector((state: PostingRootState) => namedRequestData(state, deletePostTemplateInitiated.type))

  const [published, setPublished] = useState<boolean>(false)
  const [showStatistic, setShowStatistic] = useState<boolean>(false)

  let postStatus: DeferredPostStatus | undefined = deferredPosting?.status
  let postSource: DeferredPostSource = deferredPosting?.source || DeferredPostSource.Unknown
  let postedStart: Date | undefined = deferredPosting?.postStart

  useEffect(() => {
    templateData?.publishedPosts.map((publish: PublishedPost) => {
      if (posting?.id === publish.templateId) {
        postSource = publish.source
        setPublished(true)
      }
    })
    templateData?.deferredPosts.map((deferred: DeferredPost) => {
      if (posting?.id === deferred.templateId) {
        postSource = deferred.source
        postStatus = deferred.status
        postedStart = deferred.postStart
      }
    })
  }, [templateData])

  useEffect(() => {
    storedCategories?.topics?.map((category: TopicWithMetadata) => {
      if (category.topic.active) {
        if (category.topic.automaticPostingAllowed || category.topic.manualPostingAllowed) {
          if (posting?.topicId === category.topic.id) setShowStatistic(true)
        }
      }
    })
  }, [storedCategories, templateData])

  useEffect(() => {
    ref?.current?.id === templateID && setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth' })
    }, 500)
  }, [ref])

  // Bestimme den Button-Text basierend auf dem Status
  const getButtonText = () => {
    if (props.deferredPosting) {
      if (postStatus === DeferredPostStatus.AwaitingUserConfirmation || 
          postStatus === DeferredPostStatus.NoActiveUsersForPage) {
        return t('postingCommon:acceptTemplate')
      }
      return t('postingCommon:viewTemplate')
    }
    return t('postingCommon:takeTemplate')
  }

  return (
    <Box className={classes.container}>
      <Paper className={classes.paper} ref={ref} id={posting?.id || deferredPosting?.id}>
        <Grid container>
          <Grid item container sx={{ position: 'relative' }}>
            <Chip size='small' color='primary' label={t(`postingCommon:postSource${props.filterType === FilterType.template ? 'Template' : postSource}`)} />
            <TemplatePost
              post={posting}
              user={props.user}
              filterType={props.filterType}
              deferredPost={deferredPosting}
            />
          </Grid>
          <Grid xs={12} item>
            <Divider />
            <div className={classes.footer}>
              <div className={classes.flex}>
                {posting && showStatistic &&
                  <Statistic
                    networkPageId=''
                    post={posting}
                    view={ViewEnum.single}
                    justifyContent='start'
                    networkType={NetworkType.Facebook}
                    value={{
                      postEngagedUsers: posting.engagementRate * 100,
                      postReactionsLikeTotal: posting.likes,
                      postImpressions: posting.impressionsTotal,
                      postComments: posting.comments,
                      postClicks: posting.clicks,
                      postViews: posting.views,
                      postRelativeReach: posting.relativeReach * 100,
                    }}
                  />}
                {/* geplante Posts */}
                {postStatus === DeferredPostStatus.Scheduled &&
                  <Typography>{`${t(`postingCommon:postStatus${postStatus}`)} ${postedStart && formatDate(postedStart.getTime(), getDateFormat('dateTime'))}`}</Typography>}
                {(postStatus === DeferredPostStatus.ErrorWhilePosting || postStatus === DeferredPostStatus.TokenInvalid) &&
                  <>
                    <ErrorIcon className={classes.statusColorError} />
                    <Typography className={classes.statusColorError}>{t<string>(`postingCommon:postStatus${postStatus}`)}</Typography>
                  </>}
                {(postStatus === DeferredPostStatus.AwaitingUserConfirmation || postStatus === DeferredPostStatus.NoActiveUsersForPage) &&
                  <>
                    <ReportProblemIcon className={classes.statusColorInfo} />
                    <Typography className={classes.statusColorInfo}>{t<string>(`postingCommon:postStatus${postStatus}`)}</Typography>
                  </>}
                {postStatus === DeferredPostStatus.RetryScheduled &&
                  <>
                    <ReportProblemIcon className={classes.statusColorInfo} />
                    <Typography className={classes.statusColorInfo}>{t<string>(`postingCommon:postStatus${postStatus}`)}</Typography>
                  </>}
              </div>
              <Stack direction="row" spacing={3}>
                {props.user && props.user.role !== 'EDITOR' &&
                  <ButtonComponent
                    variant='text'
                    color='info'
                    className={classes.button}
                    text={
                      <>
                        <span style={{ paddingRight: '10px' }}>
                          <VerticalSplitOutlined className={classes.icon} />
                        </span>
                        <span>{getButtonText()}</span>
                      </>
                    }
                    onClick={() => {
                      // Jetzt können auch Posts mit NoActiveUsersForPage Status bearbeitet werden
                      props.changePosting && props.changePosting(posting, deferredPosting)
                      props.changePostingScreen && props.changePostingScreen(PostingScreen.MAIN)
                    }}
                    // Button ist nicht mehr deaktiviert für NoActiveUsersForPage
                  />}
                {props.user && props.user.role !== 'USER' && !published && (
                  <Box>
                    <ButtonComponent
                      variant='text'
                      color='info'
                      className={classes.button}
                      text={
                        <>
                          <span style={{ paddingRight: '10px' }}>
                            <EditIcon className={classes.icon} />
                          </span>
                          <span>{t('postingCommon:editTemplate')}</span>
                        </>
                      }
                      onClick={() => {
                        const postId = posting?.id || deferredPosting?.id
                        postId && dispatchSetTemplateID(postId)
                        props.changePosting && props.changePosting(posting, deferredPosting)
                        props.changePostingScreen && props.changePostingScreen(PostingScreen.MAIN)
                      }}
                    />
                  </Box>
                )}
                {props.user && props.user.role !== 'USER' && posting?.id && (
                  <Box>
                    <ButtonComponent
                      text={
                        <>
                          <span style={{ paddingRight: '2px' }}>
                            <Trash className={classes.icon} width="23px"/>
                          </span>{t('postingCommon:deleteTemplate')}
                        </>
                      }
                      className={classes.button}
                      variant="text"
                      color="info"
                      onClick={() => {
                        const templateID = posting?.id
                        templateID && props.deletePosting && props.deletePosting(templateID)
                      }}
                    />
                  </Box>
                )}
              </Stack>
            </div>
            <Divider />
          </Grid>
        </Grid>
      </Paper>
      {actionOnTemplateDelete?.inProgress && actionOnTemplateDelete?.id === posting?.id &&
          <div>
            <div className={classes.backgroundLayer} />
            <div className={classes.preloader}>
              <Preloader color='secondary' />
            </div>
          </div>
      }
    </Box>
  )
}

export default ShowPostTemplate
